/**
 * Topic detail component
 */
import { switchMap } from 'rxjs/operators';

import { Location } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';

import { ProfileService } from '../services/profile.service';
import { VoxPopService } from '../services/voxpop.service';
import { Programme } from '../types/programme';
import { Topic } from '../types/topic';


@Component({
  selector: 'topic-detail',
  templateUrl: './topic-detail.component.html',
  styleUrls: ['./topic-detail.component.css'],
  providers: [VoxPopService],
  standalone: false
})

export class TopicDetailComponent implements OnInit {
  @Input() topic: Topic;
  @Input() programmes: Programme[];
  @Input() inserting: boolean;
  @Input() type_id: number;
  @Output() closeDetail: EventEmitter<any> = new EventEmitter();
  errorMessage: string;
  editable = false;
  topicDraft: Topic;
  minimumDuration: number | null = null;
  maximumDuration: number | null = null;
  saveMessage = '';
  private topic_active = false;
  private defaultMaxDuration = 30;

  constructor(
    private voxPopService: VoxPopService,
    private route: ActivatedRoute,
    private location: Location
  ) { }

  ngOnInit(): void {
    if (!ProfileService.IsLoggedIn()) { return; }

    if (!this.programmes || this.programmes.length < 1) {
      this.programmes = <Programme[]>[{ "name": "-- not set --" }];
      this.voxPopService.myActiveProgrammes().subscribe(
        programmes => {
          this.programmes = programmes;
          if (this.inserting && this.programmes.length) {
            this.topic.programme_id = this.programmes[0].id;
          }
        },
        error => this.errorMessage = <any>error,
      );
    } else {
      console.log("programmes is: " + this.programmes.length + " elements, " + JSON.stringify(this.programmes, null, 2));
    }

    if (this.inserting) {
      this.topic = new Topic();

      var maxDuration = this.programmes[0].default_recording_duration ?? this.defaultMaxDuration;
      if (maxDuration <= 0) {
        maxDuration = this.defaultMaxDuration;
      }
      this.topic.max_duration = maxDuration;

      if (this.programmes.length) {
        this.topic.programme_id = this.programmes[0].id;
      }
      this.editable = true;
      this.topic.type_id = this.type_id;
      this.edit();
    } else {
      console.log('Getting topic detail.');
      this.route.params.pipe(switchMap((params: Params) => this.voxPopService.getTopic(+params['topicid'])))
        .subscribe(
          topic => this.setTopic(topic),
          error => this.errorMessage = <any>error,
        );
    }
  }

  // ngOnChanges(changes: SimpleChanges) {
  //   for (let propName of Object.keys(changes)) {
  //     console.log('Property ' + propName + ' changed');
  //     // switch (propName) {
  //     //   case 'inHub':
  //     //     this.switchHub();
  //     //     break;
  //     // }
  //   }
  // }

  CanAdminister(): boolean {
    const topic = this.editable && this.topicDraft ? this.topicDraft : this.topic;
    if (topic.programme_id < 1) {
      return ProfileService.CanAdminister();
    }
    return ProfileService.CanAdministerProgramme(topic.programme_id);
  }

  setTopic(topic: Topic) {
    console.log('Loaded topic ' + topic.topic + '(' + topic.id + ')');
    this.topic = topic;
    this.topic_active = topic.active;
    // AppComponent.SetTitle(topic.topic + ' (' + topic.id + ')')
  }

  goBack(): void {
    this.location.back();
  }

  cancel(): void {
    this.editable = false;
    this.topicDraft = null;
    this.closeDetail.emit(null);
  }

  save(): void {
    // Empty numeric inputs stay empty in the draft; never send them as zero.
    if (this.minimumDuration == null || this.maximumDuration == null ||
      !Number.isFinite(this.minimumDuration) || !Number.isFinite(this.maximumDuration)) { return; }
    const draft = { ...this.topicDraft,
      min_duration: this.minimumDuration,
      max_duration: this.maximumDuration
    };
    if (<any>draft.active) { draft.active = true; }
    if (!<any>draft.active) { draft.active = false; }
    draft.programme_id = Number(draft.programme_id);
    this.programmes.forEach(programme => {
      if (programme.id === draft.programme_id) {
        draft.programme_key = programme.short_name;
        draft.programme_name = programme.name;
      }
    });

    if (this.inserting) {
      console.log('Creating new topic: ' + draft.topic + '(' + draft.short_name + ')');
      if (draft.active) {
        // This could be better addressed as a trigger
        draft.publication_time = new Date();
        this.topic_active = true;
      } else {
        this.topic_active = false;
      }
      this.voxPopService.createTopic(draft)
        .subscribe(
          topic => {
            this.setTopic(topic);
            this.editable = false;
            this.topicDraft = null;
            this.saveMessage = 'Topic saved.';
            this.inserting = false;
            this.closeDetail.emit(null);
          },
          error => this.errorMessage = <any>error,
        );
    } else {
      if (draft.active && !this.topic_active) {
        draft.publication_time = new Date();
        this.topic_active = true;
      } else if (!draft.active) {
        this.topic_active = false;
      }
      this.voxPopService.updateTopic(draft)
        .subscribe(() => {
          this.setTopic(draft);
          this.editable = false;
          this.topicDraft = null;
          this.saveMessage = 'Topic saved.';
        });
    }
  }

  edit(): void {
    this.topicDraft = { ...this.topic };
    this.minimumDuration = this.topic.min_duration;
    this.maximumDuration = this.topic.max_duration;
    this.saveMessage = '';
    this.editable = true;
  }

  // Topic limits are seconds in the existing API (unlike response audio duration).
  private durationParts(value: number) {
    return { hours: Math.floor(value / 3600), minutes: Math.floor(value % 3600 / 60), seconds: value % 60 };
  }

  durationLabel(value: number): string {
    if (value == null || !Number.isFinite(value)) { return 'Not specified'; }
    const parts = this.durationParts(value);
    return [parts.hours ? `${parts.hours} h` : '', parts.minutes ? `${parts.minutes} min` : '',
      parts.seconds || value === 0 ? `${parts.seconds} sec` : ''].filter(Boolean).join(' ');
  }
}
